import React, { useState, useEffect } from "react"
import { useShallow } from "zustand/react/shallow"
import { InputNumber } from "components-design-system"
import TableVersionComparisonWrapper from "../../components/TableVersionComparisonWrapper/TableVersionComparisonWrapper"
import RJTableCommentContainer from "../components/RJTableCommentContainer/RJTableCommentContainer"

import useAuthoringViewStore from "../../../hooks/stores/useAuthoringViewStore"
import useAuthoringDataStore from "../../../hooks/stores/useAuthoringDataStore"
import useAuthoringVersionStore from "../../../hooks/stores/useAuthoringVersionStore"
import useAuthoringCommentsStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringCommentsStore"

// styles
import "./RJMoreInfoAgeRange.scss"

const RJMoreInfoAgeRange = (props) => {
  const [fieldFormData, setFieldFormData] = useState({})
  const { versionData, selectedVersion } = useAuthoringVersionStore(
    useShallow((state) => ({
      versionData: state.versionData,
      selectedVersion: state.selectedVersion,
    }))
  )

  const { formData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
    }))
  )

  const { readOnly, showVersionCompare, customTableWidthClassName } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      showVersionCompare: state.showVersionCompare,
      customTableWidthClassName: state.customTableWidthClassName,
    }))
  )

  let { id } = props

  let schema = {
    headers: [
      {
        key: "age_range",
        title: "Age range",
        width: 400,
      },
      {
        key: "actual",
        title: "Actual number of subjects enrolled",
        width: 200,
      },
    ],
    properties: {
      in_utero: {
        title: "In utero *",
        properties: {
          required: true,
          type: "number",
        },
      },
      preterm_newborn_infants: {
        title: "Preterm newborn infants (gestational age < 37 wks) *",
        properties: {
          actual: {
            required: true,
            type: "number",
          },
        },
      },
      newborns: {
        title: "Newborns (0-27 days) *",
        properties: {
          actual: {
            required: true,
            type: "number",
          },
        },
      },
      infants_and_toddlers: {
        title: "Infants and toddlers (28 days-23 months) *",
        properties: {
          actual: {
            required: true,
            type: "number",
          },
        },
      },
      children: {
        title: "Children (2-11 years) *",
        properties: {
          actual: {
            required: true,
            type: "number",
          },
        },
      },
      adolescents: {
        title: "Adolescents (12-17 years) *",
        properties: {
          actual: {
            required: true,
            type: "number",
          },
        },
      },
      adults: {
        title: "Adults (18-64 years) *",
        properties: {
          actual: {
            required: true,
            type: "number",
          },
        },
      },
      elderly_65_to_84: {
        title: "From 65-84 years *",
        properties: {
          actual: {
            required: true,
            type: "number",
          },
        },
      },
      elderly_over_85: {
        title: "85 years and over *",
        properties: {
          actual: {
            required: true,
            type: "number",
          },
        },
      },
    },
  }

  useEffect(() => {
    if (formData?.results?.trial_info?.population?.population_age_group) {
      setFieldFormData(formData.results.trial_info.population.population_age_group)
    }
  }, [])

  const onChange = (e, key) => {
    let tempData = { ...fieldFormData }
    tempData[key] = e
    setFieldFormData(tempData)
    console.log("AgeRange useEffect >> onChange")
    props.onChange(tempData)
  }

  const displayRows = () => {
    return Object.keys(schema.properties).map((key) => {
      let fieldSchema = schema.properties[key]

      return (
        <tr key={"age-range-actual-" + key}>
          <Header title={fieldSchema.title} />
          <td>
            <InputNumber
              readOnly={readOnly}
              value={fieldFormData?.[key]}
              onChange={(e) => onChange(e, key)}
            />
          </td>
        </tr>
      )
    })
  }

  const displayVersionCompareRows = () => {
    return Object.keys(schema.properties).map((key) => {
      let fieldSchema = schema.properties[key]

      let compareData = versionData?.study_data?.results?.trial_info?.population?.population_age_group

      return (
        <tr key={"age-range-actual-compare" + key}>
          <td>{fieldSchema.title}</td>
          <td>{compareData?.[key] ? compareData[key] : ""}</td>
        </tr>
      )
    })
  }

  const classNames = ["more-info-age-range", "results-table", customTableWidthClassName]

  return (
    <div
      className={classNames.join(" ")}
      id={id + "-field"}
    >
      <table>
        <thead>
          <tr>
            {schema.headers.map((header, i) => {
              return (
                <th
                  key={`age-range-${header.key}`}
                  className="text-bold"
                  style={{ width: header.width }}
                >
                  {header.title}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>{displayRows()}</tbody>
      </table>

      {versionData && selectedVersion && showVersionCompare && (
        <TableVersionComparisonWrapper>
          <table>
            <thead>
              <tr>
                {schema.headers.map((header, i) => {
                  let minWidth = 200

                  return (
                    <th
                      key={`age-range-${header.key}`}
                      style={{
                        minWidth: i === 0 ? 400 : minWidth,
                      }}
                    >
                      {header.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>{displayVersionCompareRows()}</tbody>
          </table>
        </TableVersionComparisonWrapper>
      )}
    </div>
  )
}

export default RJMoreInfoAgeRange

const Header = ({ title }) => {
  const [showMenu, setShowMenu] = useState(false)
  const [openCommentsForm, setOpenCommentsForm] = useState(false)

  // let minWidth = 200;
  const { commentsMap } = useAuthoringCommentsStore(
    useShallow((state) => ({
      commentsMap: state.commentsMap,
    }))
  )

  const jpath = "results-age-info-" + title

  return (
    <td
      onMouseEnter={() => setShowMenu(true)}
      onMouseLeave={() => setShowMenu(false)}
    >
      <div className="header-content">
        {title}
        {jpath && (
          <RJTableCommentContainer
            label={`Age Range - ${title}`}
            jpath={jpath}
            openCommentsForm={openCommentsForm}
            setOpenCommentsForm={setOpenCommentsForm}
            setShowMenu={setShowMenu}
            showMenu={showMenu || commentsMap[jpath] || openCommentsForm}
            style={{ marginBottom: 2 }}
          />
        )}
      </div>
    </td>
  )
}
